'use client';

import { useMemo } from 'react';
import { Text } from '../../components/text';
import { getSeverityFgColor } from '../../components/severity-indicator';
import { Circle, HStack, VStack } from '@terra/ui-styled-system';
import type { InternalEnrichedFinding } from '../../types';
import {
  getSeverityFromScore,
  getCvssVersionFromVector,
  getFindingCvssDisplayValues,
} from '@terra/shared-cvss';

type CVSSWidgetProps = {
  finding: InternalEnrichedFinding;
  scoreLabel?: string;
  vectorLabel?: string;
  pendingText?: string;
};

export function CVSSWidget({
  finding,
  scoreLabel,
  vectorLabel,
  pendingText = 'Pending...',
}: CVSSWidgetProps) {
  const { score, vector } = useMemo(
    () => getFindingCvssDisplayValues(finding?.cvss),
    [finding?.cvss],
  );

  const cvssVersion = useMemo(() => {
    return (
      finding?.cvss?.version ??
      (vector ? getCvssVersionFromVector(vector) : undefined)
    );
  }, [finding?.cvss?.version, vector]);

  const resolvedScoreLabel =
    scoreLabel ?? (cvssVersion ? `CVSS v${cvssVersion} Score` : 'CVSS Score');
  const resolvedVectorLabel =
    vectorLabel ??
    (cvssVersion ? `CVSS v${cvssVersion} Metric Vector` : 'Metric Vector');

  const severityColor = useMemo(() => {
    const severity = score ? getSeverityFromScore(parseFloat(score)) : 'Info';
    return getSeverityFgColor(severity);
  }, [score]);

  return (
    <VStack gap={2}>
      <HStack
        justifyItems="start"
        w="full"
        alignItems="center"
        title={resolvedScoreLabel}
      >
        <Circle
          size={'64px'}
          borderWidth={4}
          style={{
            borderColor: severityColor,
          }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight="bold"
          fontSize="lg"
        >
          {score || pendingText}
        </Circle>
        <VStack gap={1} alignItems="flex-start">
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            {resolvedVectorLabel}
          </Text>
          <Text fontSize="md">{vector || 'N/A'}</Text>
        </VStack>
      </HStack>
    </VStack>
  );
}
